import {
  Form,
  useCurrentBlock,
  useFormAnswers,
} from "@quillforms/renderer-core";
import { useEffect, useRef, useState } from "react";
import formSubmissionClient from "../lib/requests";
import "@quillforms/renderer-core/build-style/style.css";
import { registerCoreBlocks } from "@quillforms/react-renderer-utils";
import "../components/quillforms/CustomFileUploadBlock";
import "../components/quillforms/CustomPhoneNumberBlock";
import formBlocks from "../forms/formBlocks"; // ✅ fixed relative path
import { createBeforeGoingNextValidator } from "../utils/validations"; // ✅ single validation function
import LiveLogger from "../utils/LiveLogger"; // ✅ fixed relative path
import "../styles.css"; // ✅ fixed relative path
import {
  trackFormLoaded,
  trackFormStarted,
  trackWelcomeStartClicked,
  trackFormSubmissionStarted,
  trackFormSubmissionSuccess,
  trackFormSubmissionFailed,
  trackFormSubmitClicked,
  trackBlockViewed,
  trackBlockAnswered,
  trackFormProgress,
} from "../utils/rum-tracking";

registerCoreBlocks();

function Screening() {
  const [ready, setReady] = useState(false);
  const [animationReady, setAnimationReady] = useState(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);


  const { answers } = useFormAnswers();
  const currentBlock = useCurrentBlock();

  useEffect(() => {
    setReady(true);
    // Track form loaded event
    trackFormLoaded();
  }, []);

  const handleAnimationStart = () => {
    setAnimationReady(false);
    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    animationTimeoutRef.current = setTimeout(() => {
      setAnimationReady(true);
    }, 500);
  };

  const hasTrackedWelcomeStartRef = useRef(false);

  useEffect(() => {
    handleAnimationStart();
    // Track block viewed when current block changes
    if (currentBlock) {
      const blockIndex = formBlocks.findIndex((b: any) => b.id === currentBlock.id);
      if (blockIndex !== -1) {
        trackBlockViewed(
          currentBlock.id,
          currentBlock.name || "unknown",
          blockIndex,
          formBlocks.length
        );

        // Track welcome start when user advances past the welcome block (block 0)
        // Welcome block has no answer, so onAnswerChange doesn't fire
        if (!hasTrackedWelcomeStartRef.current && blockIndex > 0) {
          hasTrackedWelcomeStartRef.current = true;
          trackWelcomeStartClicked();
          trackFormStarted();
        }
      }
    }
  }, [currentBlock]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!animationReady) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleClick = (e: MouseEvent) => {
      if (!animationReady) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("click", handleClick, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("click", handleClick, true);
    };
  }, [animationReady]);

  const handleSubmit = async (
    data: any,
    {
      completeForm,
      setIsSubmitting,
    }: { completeForm: () => void; setIsSubmitting: (val: boolean) => void }
  ) => {
    console.debug("[DEBUG] Form submission started", {
      timestamp: new Date().toISOString(),
    });
    const rawAnswers = data?.answers || {};

    // Track submit button clicked
    trackFormSubmitClicked();

    // Track form submission started
    const answeredCount = Object.keys(rawAnswers).length;
    trackFormSubmissionStarted({
      answeredQuestions: answeredCount,
      totalQuestions: formBlocks.length,
      formAnswers: rawAnswers,
    });

    // Basic validation
    const missingRequired = Object.entries(rawAnswers).some(
      ([blockId, a]: [string, any]) => {
        const isRequired = a?.required === true;
        const isMCQ =
          a?.blockType === "multipleChoice" || a?.blockType === "yesNo";
        const isBlank =
          !a?.value || (Array.isArray(a?.value) && a?.value.length === 0);
        return isRequired && isMCQ && isBlank;
      }
    );

    if (missingRequired) {
      alert("Please answer all required questions.");
      setIsSubmitting(false);
      return;
    }

    const transactionId = crypto.randomUUID();

    // Get parameters from SessionStorage as in parent.js
    const storage = sessionStorage.getItem("searchParams");
    const parsedUrlParams = new URLSearchParams(storage || "");

    // Get parentLocation from query parameters - now required
    const parentLocationParam = parsedUrlParams.get("parentLocation");
    if (!parentLocationParam) {
      throw new Error("parentLocation parameter is required but not provided");
    }

    const parentLocation = new URL(parentLocationParam);
    const PARENT_ORIGIN = parentLocation.origin;

    // 1. Send form data to the API via the parent window
    try {
      const response = await formSubmissionClient.submitFormToApi(
        window.parent,
        PARENT_ORIGIN,
        "formCompleted",
        {
          answers: rawAnswers,
          formId: transactionId, // Include formId as required by API
          id: transactionId, // Alternative formId field
        },
        transactionId,
      );

      console.log("Iframe: Form submission successful.", response);
      
      // Track successful submission
      trackFormSubmissionSuccess({
        transactionId,
        formAnswers: rawAnswers,
      });
      
      setIsSubmitting(false);
      completeForm();
    } catch (error: any) {
      console.error("Iframe: Form submission failed.", error);
      
      // Track failed submission
      trackFormSubmissionFailed(error?.message || "Unknown error", rawAnswers);
      
      // alert(`There was an error submitting your form, please try again: ${error.message || 'Unknown error'}`)
      setIsSubmitting(false);
      setTimeout(() => {
        const searchParamsFromStorage = sessionStorage.getItem("searchParams");
        const searchParams = searchParamsFromStorage ? new URLSearchParams(searchParamsFromStorage) : new URLSearchParams();
        window.location.href = `${window.location.origin}?${searchParams.toString()}`;
      }, 1000);
    }
  };

  if (!ready) {
    return null;
  }

  // Use refactored single validation function
  // The onBeforeNext callback fires when the user confirms an answer and advances,
  // giving us the definitive committed answer value for that block.
  const handleBeforeGoingNext = createBeforeGoingNextValidator('q7', ({ currentBlockId, answers }) => {
    const blockDef = formBlocks.find((b: any) => b.id === currentBlockId);
    const blockType = blockDef?.name ?? "unknown";
    const blockKey = blockDef?.attributes?.label;
    const answerValue = answers?.[currentBlockId]?.value;
    const hasValue =
      answerValue !== undefined && answerValue !== null && answerValue !== "";

    console.log(`[RUM] 📝 Block confirmed — ${currentBlockId} ("${blockKey}"):`, answerValue);
    trackBlockAnswered(currentBlockId, blockType, hasValue, blockKey, answerValue);
  });

  return (
    <div
      className="flex flex-col items-center justify-between min-h-screen bg-gray-50 "
      style={{
        backgroundImage: "url('/images/sleepbg1.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="flex items-center justify-center flex-1 w-full p-4 ">
        <div
          className="relative w-full max-w-2xl p-6 overflow-y-auto bg-white shadow-lg rounded-2xl"
          style={{
            height: "90vh",
          }}
        >
          <Form
            applyLogic={false}
            formId={1}
            beforeGoingNext={handleBeforeGoingNext}
            formObj={{
              blocks: formBlocks,
              messages: {
                "block.defaultThankYouScreen.label":
                  "View your results below and book your call",
              },
              settings: {
                animationDirection: "vertical",
                disableAutoSubmit: true,
                disableNavigationArrows: false,
                disableProgressBar: false,
                disableWheelSwiping: false,
                enableAutoSubmit: false,
                showLettersOnAnswers: false,
              },
              theme: {
                font: "Roboto",
                buttonsBgColor: "#522e90",
                logo: {
                  src: "./images/sax.png",
                },
                questionsColor: "#111827",
                answersColor: "#374151",
                buttonsFontColor: "#fff",
                buttonsBorderRadius: 8,
                errorsFontColor: "#b91c1c",
                errorsBgColor: "#fee2e2",
                progressBarFillColor: "#522e90",
                progressBarBgColor: "#e5e7eb",
              },
            }}
            onAnswerChange={(params: any) => {
              // Track the answered block and overall progress
              // Track the answered block and overall progress
              if (currentBlock) {
                const answersMap = (params || {}) as Record<string, { value: any }>;
                const currentAnswer = answersMap[currentBlock.id];
                const answerValue = currentAnswer?.value;
                const hasValue =
                  answerValue !== undefined &&
                  answerValue !== null &&
                  answerValue !== "";

                const blockDef = formBlocks.find((b: any) => b.id === currentBlock.id);
                const blockKey = blockDef?.attributes?.label;

                trackBlockAnswered(
                  currentBlock.id,
                  currentBlock.name || "unknown",
                  hasValue,
                  blockKey,
                  answerValue
                );

                const answeredCount = Object.keys(answersMap).filter(
                  (k) =>
                    answersMap[k]?.value !== undefined &&
                    answersMap[k]?.value !== null &&
                    answersMap[k]?.value !== ""
                ).length;

                trackFormProgress(answeredCount, formBlocks.length);
              }
            }}
            // @ts-ignore
            onSubmit={(data, event) => handleSubmit(data, event)}
          />
          <LiveLogger blocks={formBlocks} />
        </div>
      </div>
    </div>
  );
}

export default Screening;
